import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowLeft, ChevronRight, ChevronDown, RefreshCw } from 'lucide-react';
import { api } from '../lib/api';

// PATCH:poraba_v3 — poraba (Azure OCR strani, LLM tokeni, EUR) in prostor
// po plačnikih (računovodskih servisih) in bazah. Podatki: GET /superadmin/poraba

const VRSTA_LABEL: Record<string, string> = {
  ocr_read: 'read', ocr_invoice: 'invoice',
  ocr_layout: 'layout', llm_bedrock: 'LLM',
};

function danesLok(): Date { return new Date(); }
function isoD(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return d.getFullYear() + '-' + p(d.getMonth() + 1) + '-' + p(d.getDate());
}
function fmtInt(n: number): string { return (n || 0).toLocaleString('sl-SI'); }
function fmtEur(n: number): string {
  const v = n || 0;
  if (v > 0 && v < 0.01) return v.toFixed(4).replace('.', ',') + ' €';
  return v.toLocaleString('sl-SI', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}
function fmtBytes(b: number): string {
  const v = b || 0;
  if (v >= 1073741824) return (v / 1073741824).toFixed(2).replace('.', ',') + ' GB';
  if (v >= 1048576) return (v / 1048576).toFixed(1).replace('.', ',') + ' MB';
  if (v >= 1024) return Math.round(v / 1024) + ' KB';
  return v + ' B';
}
function povzetekVrst(poVrsti: any): string {
  if (!poVrsti) return '';
  return Object.entries(poVrsti).map(([k, v]: any) => {
    const oznaka = VRSTA_LABEL[k] || k;
    return k === 'llm_bedrock'
      ? oznaka + ' ' + fmtInt((v.tokens_in || 0) + (v.tokens_out || 0)) + ' tok'
      : oznaka + ' ' + fmtInt(v.strani) + ' str';
  }).join(' · ');
}

const OBDOBJA: { id: string; label: string }[] = [
  { id: 'mesec', label: 'Ta mesec' },
  { id: 'prejsnji', label: 'Prejšnji mesec' },
  { id: 'd30', label: 'Zadnjih 30 dni' },
  { id: 'leto', label: 'Letos' },
];

function razponZa(id: string): { od: string; do_: string } {
  const zdaj = danesLok();
  if (id === 'prejsnji') {
    const od = new Date(zdaj.getFullYear(), zdaj.getMonth() - 1, 1);
    const dp = new Date(zdaj.getFullYear(), zdaj.getMonth(), 0);
    return { od: isoD(od), do_: isoD(dp) };
  }
  if (id === 'd30') {
    const od = new Date(zdaj); od.setDate(od.getDate() - 29);
    return { od: isoD(od), do_: isoD(zdaj) };
  }
  if (id === 'leto') {
    return { od: zdaj.getFullYear() + '-01-01', do_: isoD(zdaj) };
  }
  return { od: isoD(new Date(zdaj.getFullYear(), zdaj.getMonth(), 1)), do_: isoD(zdaj) };
}

// PATCH:poraba_v5_trend — SVG graf poteka stroskov po bazah (brez odvisnosti)
const TREND_BARVE = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444',
  '#06b6d4', '#ec4899', '#84cc16', '#f97316', '#14b8a6'];

function lepaSkala(maks: number): number {
  if (maks <= 0) return 1;
  const eks = Math.pow(10, Math.floor(Math.log10(maks)));
  const m = maks / eks;
  return (m <= 1 ? 1 : m <= 2 ? 2 : m <= 5 ? 5 : 10) * eks;
}

function TrendGraf({ dnevi, serije, kumulativno, skrite, preklopi }: {
  dnevi: string[]; serije: any[]; kumulativno: boolean;
  skrite: Record<string, boolean>; preklopi: (naziv: string) => void;
}) {
  const W = 1000, H = 280, L = 60, R = 16, T = 12, B = 30;
  const barva = (s: any, i: number) =>
    s.naziv === 'Nepripisano' ? '#6b7280'
      : String(s.naziv || '').startsWith('Ostale baze') ? '#9ca3af'
        : TREND_BARVE[i % TREND_BARVE.length];
  const vrednostiZa = (s: any): number[] => {
    let kum = 0;
    return (s.vrednosti || []).map((v: number) => (kumulativno ? (kum += v) : v));
  };
  const vidne = serije.filter((s: any) => !skrite[s.naziv]);
  const vse = vidne.flatMap(vrednostiZa);
  const maks = lepaSkala(Math.max(0.000001, ...(vse.length ? vse : [0])));
  const x = (i: number) => (dnevi.length < 2
    ? L + (W - L - R) / 2
    : L + (i / (dnevi.length - 1)) * (W - L - R));
  const y = (v: number) => T + (1 - v / maks) * (H - T - B);
  const korakX = Math.max(1, Math.ceil(dnevi.length / 8));
  const fmtDan = (d: string) => d.slice(8, 10) + '.' + d.slice(5, 7) + '.';

  return (
    <div>
      <svg viewBox={'0 0 ' + W + ' ' + H} className="w-full h-auto select-none">
        {[0, 1, 2, 3, 4].map((i) => {
          const v = (maks / 4) * i;
          return (
            <g key={'g' + i}>
              <line x1={L} x2={W - R} y1={y(v)} y2={y(v)} strokeWidth={1}
                className="stroke-gray-200 dark:stroke-gray-700" />
              <text x={L - 6} y={y(v) + 4} textAnchor="end" fontSize={11}
                className="fill-gray-400">{fmtEur(v)}</text>
            </g>
          );
        })}
        {dnevi.map((d, i) => ((i % korakX === 0 || i === dnevi.length - 1)
          ? <text key={d} x={x(i)} y={H - 8} textAnchor="middle" fontSize={11}
              className="fill-gray-400">{fmtDan(d)}</text>
          : null))}
        {vidne.map((s: any) => {
          const vr = vrednostiZa(s);
          const b = barva(s, serije.indexOf(s));
          return (
            <g key={s.naziv}>
              <polyline fill="none" stroke={b} strokeWidth={2}
                strokeLinejoin="round" strokeLinecap="round"
                points={vr.map((v, i) => x(i) + ',' + y(v)).join(' ')} />
              {dnevi.length <= 100 && vr.map((v, i) => (
                <circle key={s.naziv + i} cx={x(i)} cy={y(v)} r={6} fill="transparent">
                  <title>{s.naziv + ' · ' + fmtDan(dnevi[i]) + ' ' + fmtEur(v)}</title>
                </circle>
              ))}
            </g>
          );
        })}
      </svg>
      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
        {serije.map((s: any, i: number) => (
          <button key={s.naziv} onClick={() => preklopi(s.naziv)} type="button"
            title={(s.servis ? s.servis + ' · ' : '') + 'klik skrije/pokaze serijo'}
            className={'inline-flex items-center gap-1.5 text-xs ' + (skrite[s.naziv] ? 'opacity-40 line-through' : '')}>
            <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: barva(s, i) }} />
            <span className="text-gray-600 dark:text-gray-300">{s.naziv}</span>
            <span className="text-gray-400 tabular-nums">{fmtEur(s.skupaj)}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

export function SuperadminPorabaPage() {
  const [obdobje, setObdobje] = useState('mesec');
  const [od, setOd] = useState(razponZa('mesec').od);
  const [doD, setDoD] = useState(razponZa('mesec').do_);
  const [podatki, setPodatki] = useState<any>(null);
  const [nalagam, setNalagam] = useState(false);
  const [odprti, setOdprti] = useState<Record<string, boolean>>({});
  const [pogled, setPogled] = useState<'placnik' | 'baza'>('placnik');
  // PATCH:poraba_v5_trend — podatki in pogled grafa poteka
  const [trend, setTrend] = useState<any>(null);
  const [trendPogled, setTrendPogled] = useState<'kum' | 'dan'>('kum');
  const [skriteSerije, setSkriteSerije] = useState<Record<string, boolean>>({});

  const nalozi = (pOd: string, pDo: string) => {
    setNalagam(true);
    api.get('/superadmin/poraba', { params: { od: pOd, do: pDo } })
      .then((r: any) => setPodatki(r.data))
      .catch((e: any) => toast.error(e?.response?.data?.detail || 'Napaka pri nalaganju porabe'))
      .finally(() => setNalagam(false));
    // PATCH:poraba_v5_trend — potek stroskov za graf (neodvisen klic)
    api.get('/superadmin/poraba/trend', { params: { od: pOd, do: pDo } })
      .then((r: any) => setTrend(r.data))
      .catch(() => setTrend(null));
  };
  useEffect(() => { nalozi(od, doD); }, []);

  const izberiObdobje = (id: string) => {
    const r = razponZa(id);
    setObdobje(id); setOd(r.od); setDoD(r.do_);
    nalozi(r.od, r.do_);
  };
  const rocnoObdobje = () => { setObdobje(''); nalozi(od, doD); };

  const skupaj = podatki?.skupaj || {};
  const placniki: any[] = podatki?.placniki || [];
  const nepripisano = podatki?.nepripisano || {};
  const maxCena = useMemo(
    () => Math.max(0.000001, ...placniki.map((p: any) => p.cena_eur || 0)),
    [placniki]);

  const bazeRavno: any[] = useMemo(() => {
    const vse: any[] = [];
    placniki.forEach((p: any) => (p.baze || []).forEach((b: any) =>
      vse.push({ ...b, servis: p.naziv })));
    vse.sort((a, b) => (b.cena_eur || 0) - (a.cena_eur || 0));
    return vse;
  }, [placniki]);

  const kartica = (label: string, vrednost: string, pripis?: string) => (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-4">
      <div className="text-xs text-gray-500 dark:text-gray-400">{label}</div>
      <div className="text-xl font-semibold text-gray-900 dark:text-white tabular-nums mt-1">{vrednost}</div>
      {pripis && <div className="text-xs text-gray-400 mt-0.5">{pripis}</div>}
    </div>
  );

  const gumb = (aktiven: boolean) =>
    'px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ' +
    (aktiven
      ? 'bg-blue-600 border-blue-600 text-white'
      : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700');

  const th = 'py-2 pr-3 font-medium';
  const tdN = 'py-2 pr-3 text-right tabular-nums';

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Poraba in prostor</h1>
          <Link to="/superadmin" className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline mt-1">
            <ArrowLeft className="w-4 h-4" /> Nazaj na Superadmin
          </Link>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {OBDOBJA.map((o) => (
            <button key={o.id} className={gumb(obdobje === o.id)} onClick={() => izberiObdobje(o.id)}>
              {o.label}
            </button>
          ))}
          <input type="date" value={od} onChange={(e) => setOd(e.target.value)}
            className="px-2 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white text-sm" />
          <span className="text-gray-400 text-sm">–</span>
          <input type="date" value={doD} onChange={(e) => setDoD(e.target.value)}
            className="px-2 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white text-sm" />
          <button onClick={rocnoObdobje} title="Osveži"
            className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
            <RefreshCw className={'w-4 h-4' + (nalagam ? ' animate-spin' : '')} />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {kartica('Dokumenti (obdobje)', fmtInt(skupaj.dokumenti))}
        {kartica('OCR strani', fmtInt(skupaj.strani), fmtInt(skupaj.klici) + ' klicev')}
        {kartica('LLM tokeni', fmtInt((skupaj.tokens_in || 0) + (skupaj.tokens_out || 0)))}
        {kartica('Stroški', fmtEur(skupaj.cena_eur), nepripisano.cena_eur > 0 ? 'od tega nepripisano ' + fmtEur(nepripisano.cena_eur) : undefined)}
        {kartica('Prostor (skupaj)', fmtBytes((skupaj.prostor_bytes || 0) + (skupaj.prostor_nepripisano_bytes || 0)), (skupaj.prostor_nepripisano_bytes || 0) > 0 ? 'od tega nepripisano ' + fmtBytes(skupaj.prostor_nepripisano_bytes) : 'kumulativno')}
      </div>

      {/* PATCH:poraba_v5_trend — graf poteka stroskov po bazah */}
      <section className="bg-white dark:bg-gray-800 rounded-xl shadow p-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-semibold text-gray-900 dark:text-white">Potek stroškov po bazah</h2>
          <div className="flex gap-2">
            <button className={gumb(trendPogled === 'kum')} onClick={() => setTrendPogled('kum')}>Kumulativno</button>
            <button className={gumb(trendPogled === 'dan')} onClick={() => setTrendPogled('dan')}>Po dnevih</button>
          </div>
        </div>
        {trend && (trend.serije || []).some((s: any) => (s.skupaj || 0) > 0) ? (
          <TrendGraf dnevi={trend.dnevi || []} serije={trend.serije || []}
            kumulativno={trendPogled === 'kum'} skrite={skriteSerije}
            preklopi={(k: string) => setSkriteSerije({ ...skriteSerije, [k]: !skriteSerije[k] })} />
        ) : (
          <div className="text-sm text-gray-400">Ni porabe v izbranem obdobju.</div>
        )}
      </section>

      <section className="bg-white dark:bg-gray-800 rounded-xl shadow p-5">
        <h2 className="font-semibold text-gray-900 dark:text-white mb-3">Stroški po plačnikih</h2>
        <div className="space-y-2">
          {placniki.map((p: any) => (
            <div key={p.tenant_id || 'x'} className="flex items-center gap-3">
              <div className="w-48 truncate text-sm text-gray-700 dark:text-gray-200" title={p.naziv}>{p.naziv}</div>
              <div className="flex-1 h-5 bg-gray-100 dark:bg-gray-700 rounded overflow-hidden">
                <div className="h-full bg-blue-500 dark:bg-blue-600"
                  style={{ width: Math.max(1, Math.round((p.cena_eur / maxCena) * 100)) + '%' }} />
              </div>
              <div className="w-24 text-right text-sm tabular-nums text-gray-900 dark:text-white">{fmtEur(p.cena_eur)}</div>
            </div>
          ))}
          {placniki.length === 0 && !nalagam && (
            <div className="text-sm text-gray-400">Ni podatkov za izbrano obdobje.</div>
          )}
        </div>
      </section>

      <section className="bg-white dark:bg-gray-800 rounded-xl shadow p-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-semibold text-gray-900 dark:text-white">Razčlenitev</h2>
          <div className="flex gap-2">
            <button className={gumb(pogled === 'placnik')} onClick={() => setPogled('placnik')}>Po plačnikih</button>
            <button className={gumb(pogled === 'baza')} onClick={() => setPogled('baza')}>Po bazah</button>
          </div>
        </div>
        <div className="overflow-x-auto">
          {pogled === 'placnik' ? (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                  <th className={th}>Plačnik</th>
                  <th className={th + ' text-right'}>Baze</th>
                  <th className={th + ' text-right'}>Dok.</th>
                  <th className={th + ' text-right'}>Strani</th>
                  <th className={th + ' text-right'}>Tokeni</th>
                  <th className={th + ' text-right'}>Prostor</th>
                  <th className={th + ' text-right'}>Cena</th>
                </tr>
              </thead>
              <tbody>
                {placniki.map((p: any) => (
                  <React.Fragment key={p.tenant_id || 'x'}>
                    <tr onClick={() => setOdprti({ ...odprti, [p.tenant_id]: !odprti[p.tenant_id] })}
                      className="border-b border-gray-100 dark:border-gray-800 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/40">
                      <td className="py-2 pr-3 font-medium text-gray-900 dark:text-white">
                        <span className="inline-flex items-center gap-1">
                          {odprti[p.tenant_id]
                            ? <ChevronDown className="w-4 h-4 text-gray-400" />
                            : <ChevronRight className="w-4 h-4 text-gray-400" />}
                          {p.naziv}
                          {p.plan && <span className="ml-1 px-1.5 py-0.5 rounded text-xs bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-300">{p.plan}</span>}
                        </span>
                      </td>
                      <td className={tdN}>{(p.baze || []).length}</td>
                      <td className={tdN}>{fmtInt(p.dokumenti)}</td>
                      <td className={tdN}>{fmtInt(p.strani)}</td>
                      <td className={tdN}>{fmtInt((p.tokens_in || 0) + (p.tokens_out || 0))}</td>
                      <td className={tdN}>{fmtBytes(p.prostor_bytes)}</td>
                      <td className={tdN + ' font-medium'}>{fmtEur(p.cena_eur)}</td>
                    </tr>
                    {odprti[p.tenant_id] && (p.baze || []).map((b: any) => (
                      <tr key={b.company_id} className="border-b border-gray-50 dark:border-gray-800/60 bg-gray-50/50 dark:bg-gray-900/30">
                        <td className="py-1.5 pr-3 pl-8 text-gray-700 dark:text-gray-300">
                          {b.naziv}
                          {b.izbrisana && <span className="ml-1 text-xs text-red-500">(izbrisana)</span>}
                          <div className="text-xs text-gray-400">{povzetekVrst(b.po_vrsti)}</div>
                        </td>
                        <td className={tdN}></td>
                        <td className={tdN}>{fmtInt(b.dokumenti)}</td>
                        <td className={tdN}>{fmtInt(b.strani)}</td>
                        <td className={tdN}>{fmtInt((b.tokens_in || 0) + (b.tokens_out || 0))}</td>
                        <td className={tdN} title={fmtInt(b.dokumenti_vsi) + ' dok. skupaj' + (b.prostor_razrez ? ' · dokumenti ' + fmtBytes(b.prostor_razrez.dokumenti || 0) + ' · logistika ' + fmtBytes(b.prostor_razrez.logistika || 0) : '')}>{fmtBytes(b.prostor_bytes)}</td>
                        <td className={tdN}>{fmtEur(b.cena_eur)}</td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                  <th className={th}>Baza</th>
                  <th className={th}>Plačnik</th>
                  <th className={th + ' text-right'}>Dok.</th>
                  <th className={th + ' text-right'}>Strani</th>
                  <th className={th + ' text-right'}>Tokeni</th>
                  <th className={th + ' text-right'}>Prostor</th>
                  <th className={th + ' text-right'}>Cena</th>
                </tr>
              </thead>
              <tbody>
                {bazeRavno.map((b: any) => (
                  <tr key={b.company_id} className="border-b border-gray-100 dark:border-gray-800">
                    <td className="py-2 pr-3 font-medium text-gray-900 dark:text-white">
                      {b.naziv}
                      {b.izbrisana && <span className="ml-1 text-xs text-red-500">(izbrisana)</span>}
                    </td>
                    <td className="py-2 pr-3 text-gray-500 dark:text-gray-400">{b.servis}</td>
                    <td className={tdN}>{fmtInt(b.dokumenti)}</td>
                    <td className={tdN}>{fmtInt(b.strani)}</td>
                    <td className={tdN}>{fmtInt((b.tokens_in || 0) + (b.tokens_out || 0))}</td>
                    <td className={tdN}>{fmtBytes(b.prostor_bytes)}</td>
                    <td className={tdN + ' font-medium'}>{fmtEur(b.cena_eur)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        {(nepripisano.cena_eur > 0 || nepripisano.klici > 0) && (
          <div className="mt-3 text-xs text-gray-400 border-t border-gray-100 dark:border-gray-800 pt-2">
            Nepripisano (razrez segmentacija, starejši logistika zapisi): {fmtInt(nepripisano.strani)} strani · {fmtEur(nepripisano.cena_eur)} — {povzetekVrst(nepripisano.po_vrsti)}
          </div>
        )}
      </section>
    </div>
  );
}
